'use client';
import { Fragment } from 'react';
import {
  CatButton,
  CatDrawer,
  CatHeading,
  CatIconArrowBack,
  CatImageGallery,
  CatImageGalleryItem,
  CatImageGalleryThumbnail,
  CatImageGalleryThumbnailItem
} from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import { v4 as uuidv4 } from 'uuid';
import { VIDEO } from '../../../constants/constants';
import ProductImageGalleryWithZoom from './product-image-gallery-with-zoom';
import styles from './product-image-zoom-drawer.module.scss';
import { GalleryItem } from '../../../types';

export type ZoomGalleryItems = Required<GalleryItem>;

export interface ProductImageZoomDrawerProps {
  drawerActive: boolean;
  closeDrawer: () => void;
  currentImageIndex?: number;
  galleryImages: ZoomGalleryItems[];
}

const ProductImageZoomDrawer = ({
  drawerActive,
  closeDrawer,
  currentImageIndex = 0,
  galleryImages
}: ProductImageZoomDrawerProps) => {
  const t = useTranslations();

  return (
    <CatDrawer
      isActive={drawerActive}
      hideCloseButton
      data-testid="image-zoom-gallery-drawer"
      orientation="side"
      className={styles['image-zoom__drawer']}
    >
      <div className="d-flex align-items-center" slot="header">
        <CatButton
          className="me-2"
          hideText
          onClick={closeDrawer}
          variant="ghost"
        >
          <CatIconArrowBack slot="before" />
        </CatButton>
        <CatHeading variant="title-sm" slot="header">
          {t('PDP_PRODUCT_IMAGES')}
        </CatHeading>
      </div>

      <div className="d-flex flex-column h-100 w-100">
        <CatImageGallery
          gridPosition="bottom"
          mobileBreakPoint={0}
          activeIndex={currentImageIndex}
        >
          <CatImageGalleryThumbnail
            className={styles['image-zoom__drawer-thumbnail']}
          >
            {galleryImages.map((image) => (
              <CatImageGalleryThumbnailItem
                key={image.id}
                className={styles['image-zoom__drawer-thumbnail-item']}
                src={image.thumbnailSrc}
                alt={image.alt}
              />
            ))}
          </CatImageGalleryThumbnail>

          {galleryImages.map((image) => (
            <Fragment key={image.id}>
              {image.type === VIDEO ? (
                <CatImageGalleryItem className="w-100">
                  <div className={styles['image-zoom__drawer-iframe']}>
                    <iframe
                      src={image.src + '?controls=1&embedded=true'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;fullscreen"
                      loading="lazy"
                      className="w-100"
                      title={uuidv4()}
                    />
                  </div>
                </CatImageGalleryItem>
              ) : (
                <ProductImageGalleryWithZoom image={image} />
              )}
            </Fragment>
          ))}
        </CatImageGallery>
      </div>
    </CatDrawer>
  );
};

export default ProductImageZoomDrawer;
